import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getQuickTaskAssigneeLabel,
  getQuickTaskAssignees,
  getQuickTaskRequester,
  getQuickTaskSourceDetail,
  getTaskDueMeta,
} from '../../admin/tasks/quickTaskUtils';
import { remindQuickTask } from '../../../api/quickTasks.ts';
import { formatDateTimeJST, toYmdSlash } from '../../../utils/datetime';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import '../../../css/common/quickTask/QuickTaskDetailModal.css';

const PRIORITY_LABELS = {
  low: '低',
  normal: '通常',
  high: '高',
  urgent: '緊急',
};

const STATUS_LABELS = {
  todo: '未完了',
  doing: '進行中',
  in_progress: '進行中',
  done: '完了',
  completed: '完了',
};

const MANUAL_REMINDER_COOLDOWN_MS = 4 * 60 * 60 * 1000;
// label=選択ピル/種類名、title=機械的な通知タイトル（送り主名の後ろに続く）、body=優しい本文。
const MANUAL_REMINDER_OPTIONS = [
  {
    key: 'progress_check',
    label: '進捗確認',
    title: '進捗確認が届きました',
    body: '対応状況を教えていただけると助かります。',
  },
  {
    key: 'please_prioritize',
    label: '対応催促',
    title: '対応催促が届きました',
    body: 'お手すきのときに、早めのご対応をお願いします。',
  },
  {
    key: 'deadline_attention',
    label: '期限リマインド',
    title: '期限リマインドが届きました',
    body: '期限が近づいています。対応状況を教えていただけますか。',
  },
  {
    key: 'please_confirm',
    label: '再度確認',
    title: '再度確認が届きました',
    body: '内容を改めてご確認いただけますか。',
  },
];

const formatDateTime = (value) => {
  if (!value) return '';
  return formatDateTimeJST(value) || String(value).slice(0, 16);
};

const isAdminAssigneeForUser = (assignee, adminId) => (
  adminId &&
  String(assignee?.id || assignee?.adminUserId || assignee?.admin_user_id || '') === String(adminId)
);

export default function QuickTaskDetailModal({
  open,
  task,
  audience = 'admin',
  completing = false,
  onClose,
  onComplete,
  onAskRequester,
  onReminderSent,
}) {
  const { user } = useAuth() || {};
  const currentAdminId = user?.userId ?? user?.id ?? null;
  const [selectedReminderKey, setSelectedReminderKey] = useState('progress_check');
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [reminderSuccess, setReminderSuccess] = useState('');
  const manualReminderState = useMemo(() => {
    const reminders = Array.isArray(task?.reminders) ? task.reminders : [];
    const manual = reminders
      .filter((item) => (
        String(item?.note || '').startsWith('manual:') &&
        String(item?.createdByAdminId || item?.created_by_admin_id || '') === String(currentAdminId || '')
      ))
      .map((item) => item?.createdAt || item?.created_at || item?.sentAt || item?.sent_at || item?.remindAt || item?.remind_at)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    const lastSentAt = manual[0] || null;
    const nextAvailableAt = lastSentAt ? new Date(new Date(lastSentAt).getTime() + MANUAL_REMINDER_COOLDOWN_MS) : null;
    const blocked = nextAvailableAt && nextAvailableAt.getTime() > Date.now();
    return {
      lastSentAt,
      nextAvailableAt: blocked ? nextAvailableAt.toISOString() : '',
      blocked,
    };
  }, [currentAdminId, task]);

  if (!open || !task) return null;

  const taskId = String(task?.id || task?.taskId || '');
  const statusKey = String(task?.status || 'todo').trim().toLowerCase();
  const completed = Boolean(task?.completedAt || statusKey === 'done' || statusKey === 'completed');
  const priorityKey = String(task?.priority || 'normal').trim().toLowerCase();
  const dueMeta = getTaskDueMeta(task);
  const isArtistAudience = audience === 'artist';
  const sourceDetail = isArtistAudience
    ? { typeLabel: '', rows: [] }
    : getQuickTaskSourceDetail(task, { includeInternalIds: true });
  const assignees = getQuickTaskAssignees(task);
  const assigneeLabel = getQuickTaskAssigneeLabel(task);
  const requester = getQuickTaskRequester(task);
  const requestedByAdminId = task?.requestedByAdminId || task?.requested_by_admin_id || requester?.id || '';
  const createdAt = formatDateTime(task?.createdAt || task?.created_at);
  const completedAt = formatDateTime(task?.completedAt || task?.completed_at);
  const dueLabel = dueMeta.hasDue ? toYmdSlash(task?.dueOn || task?.due_on) : '';
  const reminderOption = MANUAL_REMINDER_OPTIONS.find((option) => option.key === selectedReminderKey) || MANUAL_REMINDER_OPTIONS[0];
  const requesterName = requester?.displayName || task?.requestedBy || '依頼者';
  const isSelfRequestedAdmin = Boolean(
    !isArtistAudience &&
      currentAdminId &&
      requestedByAdminId &&
      String(currentAdminId) === String(requestedByAdminId)
  );
  const isSelfOwnedAdminTask = Boolean(
    isSelfRequestedAdmin &&
      assignees.length === 1 &&
      assignees.every((assignee) => assignee?.type === 'admin' && isAdminAssigneeForUser(assignee, currentAdminId))
  );
  const canShowManualReminder = (
    !isArtistAudience &&
    !completed &&
    currentAdminId &&
    requestedByAdminId &&
    String(currentAdminId) === String(requestedByAdminId) &&
    !isSelfOwnedAdminTask &&
    assignees.length > 0
  );
  const canAskRequester = Boolean(
    isArtistAudience &&
      onAskRequester &&
      requester &&
      requestedByAdminId &&
      requesterName
  );
  const deadlineStatusLabel = completed
    ? '完了済み'
    : dueMeta.hasDue
      ? (dueMeta.countdownLabel || dueLabel)
      : '期限なし';
  const deadlineStatusTone = completed ? 'done' : (dueMeta.stage || 'none');
  // 送り主名は受け手に見える実名を表示する（閲覧者＝依頼者本人なので自分の氏名になる）。
  const currentAdminName = String(
    user?.full_name || user?.nickname || user?.username || user?.name || ''
  ).trim();
  const reminderSenderName =
    (requesterName && requesterName !== '依頼者' ? requesterName : '') ||
    currentAdminName ||
    '依頼者';
  const reminderPreviewTitle = `${reminderSenderName}さんから${reminderOption.title}`;

  const handleComplete = () => {
    if (!taskId || completed || completing) return;
    onComplete?.(taskId);
  };

  const handleReminderSend = async () => {
    if (!taskId || reminderSending || manualReminderState.blocked) return;
    setReminderSending(true);
    setReminderError('');
    setReminderSuccess('');
    try {
      const result = await remindQuickTask(taskId, { messageKey: selectedReminderKey });
      setReminderSuccess('リマインドを送信しました。');
      onReminderSent?.(result?.task || task, result);
    } catch (error) {
      setReminderError(error?.message || 'リマインドの送信に失敗しました。');
    } finally {
      setReminderSending(false);
    }
  };

  const handleAskRequester = () => {
    if (!canAskRequester) return;
    onAskRequester?.({ task, requester });
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title="クイックタスク詳細"
      className="qt-detail-modal"
    >
        <div className={`qt-detail__deadlineStatus is-${deadlineStatusTone}`} aria-label="期限ステータス">
          <span>期限ステータス</span>
          <strong>{deadlineStatusLabel}</strong>
          {dueLabel ? <small>期限 {dueLabel}</small> : null}
        </div>

        <div className="qt-detail__mainTask">
          {!isArtistAudience && sourceDetail.typeLabel ? (
            <span className="qt-detail__eyebrow">{sourceDetail.typeLabel}</span>
          ) : null}
          <h3>{task?.title || 'タスク'}</h3>
          {task?.description ? (
            <p className="qt-detail__description">{task.description}</p>
          ) : null}
        </div>

        <div className="qt-detail__metaList" aria-label="メタ情報">
          <div><span>状態</span><strong>{STATUS_LABELS[statusKey] || '未完了'}</strong></div>
          <div><span>優先度</span><strong>{PRIORITY_LABELS[priorityKey] || PRIORITY_LABELS.normal}</strong></div>
          {dueLabel ? <div><span>期限</span><strong>{dueLabel}</strong></div> : null}
          {!isArtistAudience && createdAt ? <div><span>作成日時</span><strong>{createdAt}</strong></div> : null}
          {completedAt ? <div><span>完了日時</span><strong>{completedAt}</strong></div> : null}
          {!isSelfRequestedAdmin && requester ? (
            <div><span>依頼者</span><strong>{requester.displayName}</strong></div>
          ) : null}
          {!isArtistAudience ? <div><span>担当</span><strong>{assigneeLabel}</strong></div> : null}
        </div>

        {sourceDetail.rows.length ? (
          <div className="qt-detail__source">
            <span className="qt-detail__sectionTitle">関連情報</span>
            {sourceDetail.rows.map((row) => (
              <div className="qt-detail__infoRow" key={`${row.label}:${row.value}`}>
                <span>{row.label}</span>
                {row.href ? (
                  <Link className="qt-detail__infoLink" to={row.href} onClick={onClose}>
                    {row.value}
                    <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                  </Link>
                ) : (
                  <strong>{row.value}</strong>
                )}
              </div>
            ))}
          </div>
        ) : null}

        {canAskRequester ? (
          <div className="qt-detail__artistQuestion">
            <div>
              <strong>内容について質問する</strong>
              <span>{requesterName}さんへのメンションを入力した状態でTacticartチャットを開きます。</span>
            </div>
            <DsButton type="button" variant="secondary" size="sm" onClick={handleAskRequester}>
              Tacticartチャットへ
            </DsButton>
          </div>
        ) : null}

        {canShowManualReminder ? (
          <div className="qt-detail__reminder">
            <span className="qt-detail__sectionTitle">依頼先へリマインド</span>
            <div className="qt-detail__reminderOptions" role="radiogroup" aria-label="リマインド文言">
              {MANUAL_REMINDER_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`qt-detail__reminderOption ${selectedReminderKey === option.key ? 'is-active' : ''}`}
                  onClick={() => setSelectedReminderKey(option.key)}
                  disabled={reminderSending || manualReminderState.blocked}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="qt-detail__reminderPreview">
              <strong>通知プレビュー</strong>
              <span>{reminderPreviewTitle}</span>
              <p>{reminderOption.label}：{task?.title || 'クイックタスク'}</p>
              <small>{reminderOption.body}</small>
            </div>
            {manualReminderState.blocked ? (
              <p className="qt-detail__reminderNotice">
                送信済みです。次回は {formatDateTime(manualReminderState.nextAvailableAt)} 以降に送信できます。
              </p>
            ) : null}
            {reminderError ? <p className="qt-detail__reminderError">{reminderError}</p> : null}
            {reminderSuccess ? <p className="qt-detail__reminderSuccess">{reminderSuccess}</p> : null}
            <DsButton
              type="button"
              size="sm"
              className="qt-detail__reminderSend"
              onClick={handleReminderSend}
              disabled={reminderSending || manualReminderState.blocked}
              loading={reminderSending}
            >
              {reminderSending ? '送信中…' : 'この内容でリマインドを送る'}
            </DsButton>
          </div>
        ) : null}

        {!completed ? (
          <div className="qt-detail__completeArea">
            <DsButton type="button" block onClick={handleComplete} disabled={completing} loading={completing}>
              {completing ? '完了処理中…' : '完了にする'}
            </DsButton>
          </div>
        ) : null}

    </DsModal>
  );
}
